/**
 * SymbolPalette.tsx — what may be placed, and where it came from.
 *
 * The palette lists the definitions the SERVER says this project has. It does
 * not ship a symbol library of its own, and it cannot: symbols come from the
 * controlled registry through the project's manifest and lock, and a browser
 * that carried its own copy would be a second, unversioned source of the same
 * symbols.
 *
 * That is why every entry shows its registry dependency when it has one — the
 * alias, and the exact version the project locked. A symbol with no dependency
 * is project-local, which is a real and different thing, so it says that too.
 */

import { useMemo, useState } from "react";
import { Empty, Panel } from "../../components/Panel";
import type { SymbolCatalogEntry } from "../../types/contract";

export function SymbolPalette({ entries, armedDefId, onArm, disabled }: {
  entries: SymbolCatalogEntry[] | undefined;
  armedDefId: string | undefined;
  onArm: (entry: SymbolCatalogEntry | undefined) => void;
  disabled?: boolean;
}): JSX.Element {
  const [filter, setFilter] = useState("");

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const all = entries ?? [];
    if (!needle) return all.slice(0, 250);
    return all
      .filter(entry =>
        entry.defId.toLowerCase().includes(needle) ||
        entry.name.toLowerCase().includes(needle) ||
        entry.family.toLowerCase().includes(needle) ||
        entry.domain.toLowerCase().includes(needle))
      .slice(0, 250);
  }, [entries, filter]);

  return (
    <Panel
      title="Symbols"
      actions={armedDefId ? <button type="button" className="ghost" onClick={() => onArm(undefined)}>Cancel placement</button> : undefined}
    >
      <input
        type="search" value={filter} placeholder="Filter by name, family or domain"
        onChange={event => setFilter(event.target.value)}
        aria-label="Filter symbols"
      />
      {!entries ? <Empty>Loading…</Empty> : visible.length === 0 ? <Empty>Nothing matches.</Empty> : (
        <ul className="palette">
          {visible.map(entry => (
            <li key={entry.defId}>
              <button
                type="button"
                className={entry.defId === armedDefId ? "palette-entry is-armed" : "palette-entry"}
                onClick={() => onArm(entry.defId === armedDefId ? undefined : entry)}
                disabled={disabled}
                title={entry.defId}
              >
                <span className="palette-name">{entry.name || entry.defId}</span>
                <span className="palette-meta">
                  {entry.domain} · {entry.family} · {entry.portCount} ports
                </span>
                <span className="palette-origin">
                  {entry.dependency
                    ? `${entry.dependency.alias} @ ${entry.dependency.version}`
                    : "project-local"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {entries && entries.length > visible.length && (
        <p className="muted">{entries.length - visible.length} more — narrow the filter.</p>
      )}
    </Panel>
  );
}
